import React, { useMemo, useState } from 'react';
import { motion } from 'motion/react';
import { CONDITION_MAP } from '../components/helpers';
import {
  Page, Hero, StatStrip, ControlRail, Segmented, Empty, Thumb, Meter, Spark,
  useReveal, MoreButton,
  findSkinImage, money, pct, dateShort,
} from './mobileShared';

// Watchboard — skins being waited on. The card leads with the gap to target
// because that is the only thing being decided here: buy now, or keep waiting.

function getWishPrice(livePrices, item) {
  if (!livePrices || !item.skin) return null;
  if (item.condition) {
    const cond = CONDITION_MAP[item.condition] || item.condition;
    return (livePrices[`${item.skin} (${cond})`] || livePrices[item.skin])?.starting_at?.price ?? null;
  }
  return livePrices[item.skin]?.starting_at?.price ?? null;
}

const FILTERS = [
  { key: 'all',     label: 'All' },
  { key: 'hit',     label: 'Target hit' },
  { key: 'waiting', label: 'Waiting' },
];

export default function MobileWishlist({ wishlist, livePrices, skinsData = [] }) {
  const [query, setQuery]   = useState('');
  const [filter, setFilter] = useState('all');

  const items = useMemo(() => (wishlist || []).map(w => {
    const price = getWishPrice(livePrices, w);
    const hit   = price != null && w.targetPrice != null && price <= w.targetPrice;
    const hist  = (w.priceHistory || []).map(d => d.price).filter(p => p != null);
    const high  = hist.length ? Math.max(...hist, price ?? 0) : price;
    let progress = null;
    if (price != null && w.targetPrice != null && high != null && high > w.targetPrice) {
      progress = Math.max(0, Math.min(1, (high - price) / (high - w.targetPrice)));
    } else if (hit) progress = 1;
    const gapPct = price != null && w.targetPrice ? ((price - w.targetPrice) / w.targetPrice) * 100 : null;
    return { ...w, price, hit, progress, gapPct };
  }).sort((a, b) => (b.hit - a.hit) || ((a.gapPct ?? Infinity) - (b.gapPct ?? Infinity))),
  [wishlist, livePrices]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter(w => {
      if (filter === 'hit' && !w.hit) return false;
      if (filter === 'waiting' && w.hit) return false;
      if (q && !w.skin.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, query, filter]);

  const hits = items.filter(w => w.hit).length;

  const totals = useMemo(() => {
    const withTarget = items.filter(w => w.targetPrice != null);
    const budget = withTarget.reduce((s, w) => s + w.targetPrice, 0);
    const now    = items.reduce((s, w) => s + (w.price ?? 0), 0);
    const gaps   = withTarget.map(w => w.gapPct).filter(g => g != null);
    return { budget, now, avgGap: gaps.length ? gaps.reduce((s, g) => s + g, 0) / gaps.length : null };
  }, [items]);

  const [shown, hasMore, showMore] = useReveal(filtered, 10);

  if (!items.length) {
    return (
      <Page>
        <Empty>The watchboard is empty. Add wishes on desktop and track them here.</Empty>
      </Page>
    );
  }

  return (
    <Page>
      <Hero
        label="Ready to buy"
        value={String(hits)}
        tone={hits > 0 ? 'up' : 'neutral'}
        sub={`of ${items.length} watched skin${items.length !== 1 ? 's' : ''}`}
        foot={totals.avgGap != null
          ? `on average ${pct(totals.avgGap)} above target`
          : 'no live prices yet'}
      />

      <StatStrip items={[
        { label: 'Target budget', value: money(totals.budget), sub: 'if every target filled' },
        { label: 'Asking now', value: money(totals.now), sub: 'current market total' },
        { label: 'Watching', value: String(items.length), sub: `${hits} at target` },
      ]} />

      <ControlRail query={query} onQuery={setQuery} placeholder="Search watchboard…" />
      <Segmented
        options={FILTERS.map(f => (f.key === 'hit' && hits > 0 ? { ...f, label: `Target hit (${hits})` } : f))}
        value={filter}
        onChange={setFilter}
      />

      {filtered.length === 0 ? (
        <Empty>Nothing matches your filters.</Empty>
      ) : (
        <>
          <div className="mb-list">
            {shown.map((w, i) => {
              const img = findSkinImage(skinsData, w.skin, w.condition);
              return (
                <motion.article
                  key={w.id || i}
                  className={`mb-wish ${w.hit ? 'hit' : ''}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1], delay: Math.min(i, 8) * 0.035 }}
                >
                  {w.hit && <span className="mb-wish-badge">TARGET HIT</span>}
                  <div className="mb-wish-top">
                    <Thumb src={img} />
                    <div className="mb-row-main">
                      <div className="mb-row-name">{w.skin}</div>
                      <div className="mb-row-meta">
                        <span>{w.condition || 'Any condition'}</span>
                        <span>since {w.createdAt ? dateShort(w.createdAt) : 'n/a'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mb-wish-prices">
                    <span className="cur">{w.price != null ? money(w.price) : 'n/a'}</span>
                    <span className="tgt">
                      {w.targetPrice != null ? `target ${money(w.targetPrice)}` : 'no target set'}
                    </span>
                  </div>

                  {w.progress != null && (
                    <>
                      <Meter
                        ratio={w.progress}
                        color={w.hit ? 'var(--green-dim)' : 'linear-gradient(90deg, var(--indigo), var(--green-dim))'}
                        delay={0.15 + Math.min(i, 8) * 0.03}
                      />
                      <div className="mb-wish-gap" style={{ color: w.hit ? 'var(--green-dim)' : undefined }}>
                        {w.hit
                          ? `${Math.abs(w.gapPct).toFixed(1)}% under target — time to buy?`
                          : w.gapPct != null ? `${w.gapPct.toFixed(1)}% above target` : ''}
                      </div>
                    </>
                  )}

                  <div className="mb-wish-foot">
                    <span className="lbl">Price trail</span>
                    <Spark values={(w.priceHistory || []).map(d => d.price)} invert />
                  </div>

                  {w.note && <p className="mb-wish-note">“{w.note}”</p>}
                </motion.article>
              );
            })}
          </div>
          {hasMore && <MoreButton onClick={showMore} remaining={filtered.length - shown.length} />}
        </>
      )}
    </Page>
  );
}
